import * as Sentry from '@sentry/browser';
import { LitElement, html } from 'lit';
import { formatCurrency } from '../../utils/format-utils.js';
import '../icons/info-icon.js';

/**
 * @component payment-installments
 * @description Procuret installment payment card — loads installment options from API,
 * displays available plans, and redirects to Procuret application.
 *
 * @property {String} quoteReference - Quote reference number
 * @property {String} totalAmount - Formatted total (e.g. "$759.00")
 * @property {String} contactEmail - Customer email for pre-fill
 * @property {Boolean} meetsMinimum - Whether amount meets minimum threshold
 * @property {String} minimumFormatted - Formatted minimum amount for display
 */
class PaymentInstallments extends LitElement {
    static properties = {
        quoteReference: { type: String },
        totalAmount: { type: String },
        contactEmail: { type: String },
        meetsMinimum: { type: Boolean },
        minimumFormatted: { type: String },
        // Internal state
        installmentOptions: { type: Array, state: true },
        isLoading: { type: Boolean, state: true },
        error: { type: Boolean, state: true },
        apiError: { type: String, state: true },
    };

    createRenderRoot() {
        return this;
    }

    constructor() {
        super();
        this.quoteReference = '';
        this.totalAmount = '$0.00';
        this.contactEmail = '';
        this.meetsMinimum = false;
        this.minimumFormatted = '$0.00';
        this.installmentOptions = [];
        this.isLoading = false;
        this.error = false;
        this.apiError = null;
    }

    get paymentAmount() {
        return this.totalAmount.replace(/[$,]/g, '');
    }

    connectedCallback() {
        super.connectedCallback();
        if (this.meetsMinimum) {
            this._loadOptions();
        }
    }

    _formatNZD(amount) {
        return formatCurrency(parseFloat(amount));
    }

    _handleLogoError(e) {
        e.target.style.display = 'none';
    }

    async _loadOptions() {
        this.isLoading = true;
        this.error = false;
        this.apiError = null;

        const supplierId = import.meta.env.VITE_PROCURET_SUPPLIER_ID;
        const baseUrl = import.meta.env.VITE_PROCURET_BASE_URL;

        if (!supplierId || !baseUrl) {
            console.error('Procuret env vars not configured');
            this.error = true;
            this.isLoading = false;
            return;
        }

        const timeoutId = setTimeout(() => {
            if (this.isLoading) {
                console.error('Procuret API request timed out');
                this.isLoading = false;
                this.error = true;

                Sentry.captureMessage('Procuret API timeout', {
                    level: 'error',
                    tags: {
                        payment_method: 'installments',
                        quote_reference: this.quoteReference,
                    },
                    contexts: {
                        procuret: {
                            amount: this.paymentAmount,
                            supplier_id: supplierId,
                        },
                    },
                });

                this.requestUpdate();
            }
        }, 10000);

        try {
            const url = new URL(`${baseUrl}/api/credit/prospective-payment/list`);
            url.searchParams.set('cycle', '1');
            url.searchParams.set('supplier_id', supplierId);
            url.searchParams.set('principle', this.paymentAmount);
            url.searchParams.set('denomination', '2');

            const response = await fetch(url, {
                method: 'GET',
                headers: { Accept: 'application/json' },
            });

            clearTimeout(timeoutId);
            this.isLoading = false;

            if (!response.ok) {
                let apiErrorInfo = null;
                try {
                    const errorData = await response.json();
                    apiErrorInfo = errorData['error-information'] || null;
                } catch {
                    // Response body not JSON
                }

                console.error(`Procuret API error: HTTP ${response.status}`);
                this.error = true;
                this.apiError = apiErrorInfo;

                Sentry.captureMessage('Procuret API error', {
                    level: 'error',
                    tags: {
                        payment_method: 'installments',
                        quote_reference: this.quoteReference,
                        error_status: response.status,
                    },
                    contexts: {
                        procuret: {
                            amount: this.paymentAmount,
                            supplier_id: supplierId,
                            api_error_info: apiErrorInfo,
                        },
                    },
                });
                return;
            }

            const data = await response.json();

            if (!Array.isArray(data)) {
                console.error('Invalid Procuret API response format:', data);
                this.error = true;
                Sentry.captureMessage('Invalid Procuret API response', {
                    level: 'error',
                    tags: { payment_method: 'installments', quote_reference: this.quoteReference },
                });
                return;
            }

            if (data.length === 0) {
                this.installmentOptions = [];
                this.requestUpdate();
                return;
            }

            this.installmentOptions = data
                .map((option) => ({
                    periods: option.periods,
                    monthlyPayment: this._formatNZD(option.payment),
                }))
                .sort((a, b) => a.periods - b.periods);

            Sentry.addBreadcrumb({
                category: 'payment-installments',
                message: `Loaded ${this.installmentOptions.length} installment options`,
                data: {
                    quoteReference: this.quoteReference,
                    periods: this.installmentOptions.map((o) => o.periods),
                },
            });

            this.requestUpdate();
        } catch (error) {
            clearTimeout(timeoutId);
            console.error('Error calling Procuret API:', error);
            this.isLoading = false;
            this.error = true;

            Sentry.captureException(error, {
                tags: {
                    payment_method: 'installments',
                    quote_reference: this.quoteReference,
                },
            });

            this.requestUpdate();
        }
    }

    _handleApply() {
        const supplierId = import.meta.env.VITE_PROCURET_SUPPLIER_ID;
        const baseUrl = import.meta.env.VITE_PROCURET_BASE_URL;

        Sentry.addBreadcrumb({
            category: 'payment-installments',
            message: 'Installment payment initiated',
            data: {
                quoteReference: this.quoteReference,
                amount: this.paymentAmount,
                availablePeriods: this.installmentOptions.map((o) => o.periods),
            },
        });

        let procuretUrl = `${baseUrl}/b?supplier_id=${supplierId}&ii=${this.quoteReference}&ia=${this.paymentAmount}&id=NZD`;
        if (this.contactEmail) {
            procuretUrl = `${procuretUrl}&email=${encodeURIComponent(this.contactEmail)}`;
        }

        window.open(procuretUrl, '_blank', 'noopener,noreferrer');
    }

    renderHeader() {
        return html`
            <div
                class="flex flex-col items-start sm:flex-row sm:justify-between sm:items-center gap-3 mb-4"
            >
                <img
                    src="/reachpay.png"
                    alt="REACH PAY"
                    class="h-6 sm:order-last self-center sm:self-auto"
                    @error="${this._handleLogoError}"
                />
                <h3 class="card-title text-xl">Pay in Installments</h3>
            </div>
        `;
    }

    renderBelowMinimum() {
        return html`
            <div class="card bg-base-200">
                <div class="card-body">
                    ${this.renderHeader()}
                    <div class="alert alert-row alert-info">
                        <info-icon class="shrink-0"></info-icon>
                        <span class="text-sm text-left">
                            Installment payment options are available for quotes of
                            <strong>${this.minimumFormatted}</strong> or more.
                        </span>
                    </div>
                </div>
            </div>
        `;
    }

    renderContent() {
        if (this.isLoading) {
            return html`
                <div class="flex items-center justify-center py-8">
                    <span class="loading loading-spinner loading-lg"></span>
                    <span class="ml-3">Loading installment options...</span>
                </div>
            `;
        }

        if (this.error) {
            return html`
                <div class="alert alert-row alert-warning">
                    <info-icon class="shrink-0"></info-icon>
                    <div class="text-sm text-left">
                        <p>
                            Unable to load installment options at this time. Please try another
                            payment method.
                        </p>
                        ${this.apiError
                            ? html`
                                  <p class="text-xs mt-2 opacity-75">API Error: ${this.apiError}</p>
                              `
                            : ''}
                    </div>
                </div>
            `;
        }

        if (this.installmentOptions.length === 0) {
            return html`
                <div class="alert alert-row alert-info">
                    <info-icon class="shrink-0"></info-icon>
                    <span class="text-sm text-left">
                        No installment options available for this amount.
                    </span>
                </div>
            `;
        }

        return html`
            <div class="mb-4">
                <p class="text-sm text-gray-600 mb-3">Available installment options:</p>
                <div class="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3">
                    ${this.installmentOptions.map(
                        (option) => html`
                            <div class="card bg-base-100 border border-base-300 shadow-sm">
                                <div class="card-body p-4 text-center">
                                    <p class="font-bold text-lg">${option.periods} months</p>
                                    <p class="text-sm text-gray-600">${option.monthlyPayment}/mo</p>
                                </div>
                            </div>
                        `
                    )}
                </div>
            </div>

            <button @click="${this._handleApply}" class="btn btn-primary btn-outline">
                Apply for Installments
            </button>

            <div class="alert alert-row alert-info mt-4">
                <info-icon class="shrink-0"></info-icon>
                <span class="text-sm text-left">
                    Clicking <strong>Apply for Installments</strong> will open the Procuret
                    application in a new window where you can select your preferred payment plan.
                    Subject to approval.
                </span>
            </div>
        `;
    }

    render() {
        if (!this.meetsMinimum) {
            return this.renderBelowMinimum();
        }

        return html`
            <div class="card bg-base-200">
                <div class="card-body">${this.renderHeader()} ${this.renderContent()}</div>
            </div>
        `;
    }
}

customElements.define('payment-installments', PaymentInstallments);
